interface easingFunctionsType {
  [key: string]: [number, number, number, number]
}
export const easingFunctions: easingFunctionsType = {
  easeInQuint: [0.755, 0.05, 0.855, 0.06],
  easeOutQuint: [0.23, 1, 0.32, 1],
  easeInOutQuint: [0.86, 0, 0.07, 1],
  easeInQuad: [0.55, 0.085, 0.68, 0.53],
  easeOutQuad: [0.25, 0.46, 0.45, 0.94],
  easeInOutQuad: [0.455, 0.03, 0.515, 0.955],
  easeInSine: [0.47, 0, 0.745, 0.715],
  easeOutSine: [0.39, 0.575, 0.565, 1],
  easeInOutSine: [0.445, 0.05, 0.55, 0.95],
  easeInOutCw: [0, 0.9, 0.1, 1],
  easeOutBack: [0.175, 0.885, 0.32, 1.275],
  easeInOutBack: [0.68, -0.55, 0.265, 1.55],
  easeInBack: [0.36, 0, 0.66, -0.56],
  linear: [0, 0, 1, 1],
  custom: [0.58, 0.245, 0.06, 0.965],
}

//type EaseType = 'css' | 'array' | 'object'
type EasingFunctionName =
  | 'easeInQuint'
  | 'easeOutQuint'
  | 'easeInOutQuint'
  | 'easeInQuad'
  | 'easeOutQuad'
  | 'easeInOutQuad'
  | 'easeInSine'
  | 'easeOutSine'
  | 'easeInOutSine'
  | 'easeInOutCw'
  | 'easeOutBack'
  | 'easeInOutBack'
  | 'easeInBack'
  | 'linear'
  | 'custom'

type EaseArgs = {
  duration?: number
  delay?: number
}

export const easeUse = (name: EasingFunctionName, args?: EaseArgs) => {
  const easingFunctionArray = easingFunctions[name]

  if (!easingFunctionArray) {
    throw new Error(`Easing function "${name}" not found.`)
  }

  //const easingFunctionCSS = `cubic-bezier(${easingFunctionArray.join(',')})`

  return {
    type: 'tween',
    duration: args?.duration ?? 0.8,
    ease: easingFunctionArray,
    delay: args?.delay ?? 0,
  }

}

export function easeOutBackFunc(x: number): number {
  return 1 - (1 - x) * (1 - x)
}

export function easeOutQuintF(x: number): number {
  return 1 - Math.pow(1 - x, 5)
}

export function easeInOutQuintF(x: number): number {
  return x < 0.5 ? 16 * x * x * x * x * x : 1 - Math.pow(-2 * x + 2, 5) / 2
}

export function easeOutInQuintF(x: number): number {
  if (x < 0.5) {
    // ease out
    return (Math.pow(2 * x - 1, 5) + 1) / 2
  } else {
    // ease in
    return Math.pow(2 * x - 1, 5) / 2
  }
}

export const easingF = {
  /** default */
  default: function (t: number, b = 0, c = 1, d = 1) {
    return (c * t) / d + b
  },
  /** linear */
  linear: function (t: number, b = 0, c = 1, d = 1) {
    return (c * t) / d + b
  },
  /** swing */
  swing: function (t: number, b = 0, c = 1, d = 1) {
    return -c * (t /= d) * (t - 2) + b
  },
  /** easeInQuad */
  easeInQuad: function (t: number, b = 0, c = 1, d = 1) {
    return c * (t /= d) * t + b
  },
  /** easeOutQuad */
  easeOutQuad: function (t: number, b = 0, c = 1, d = 1) {
    return -c * (t /= d) * (t - 2) + b
  },
  /** easeInOutQuad */
  easeInOutQuad: function (t: number, b = 0, c = 1, d = 1) {
    if ((t /= d / 2) < 1) return (c / 2) * t * t + b
    return (-c / 2) * (--t * (t - 2) - 1) + b
  },
  /** defeInCubic */
  easeInCubic: function (t: number, b = 0, c = 1, d = 1) {
    return c * (t /= d) * t * t + b
  },
  /** easeOutCubic */
  easeOutCubic: function (t: number, b = 0, c = 1, d = 1) {
    return c * ((t = t / d - 1) * t * t + 1) + b
  },
  /** easeInOutCubic */
  easeInOutCubic: function (t: number, b = 0, c = 1, d = 1) {
    if ((t /= d / 2) < 1) return (c / 2) * t * t * t + b
    return (c / 2) * ((t -= 2) * t * t + 2) + b
  },
  /** easeOutInCubic */
  easeOutInCubic: function (t: number, b = 0, c = 1, d = 1) {
    if (t < d / 2) return easingF.easeOutCubic(t * 2, b, c / 2, d)
    return easingF.easeInCubic(t * 2 - d, b + c / 2, c / 2, d)
  },
  /** easeInQuart */
  easeInQuart: function (t: number, b = 0, c = 1, d = 1) {
    return c * (t /= d) * t * t * t + b
  },
  /** easeOutQuart */
  easeOutQuart: function (t: number, b = 0, c = 1, d = 1) {
    return -c * ((t = t / d - 1) * t * t * t - 1) + b
  },
  /** easeInOutQuart */
  easeInOutQuart: function (t: number, b = 0, c = 1, d = 1) {
    if ((t /= d / 2) < 1) return (c / 2) * t * t * t * t + b
    return (-c / 2) * ((t -= 2) * t * t * t - 2) + b
  },
  /** easeOutInQuart */
  easeOutInQuart: function (t: number, b = 0, c = 1, d = 1) {
    if (t < d / 2) return easingF.easeOutQuart(t * 2, b, c / 2, d)
    return easingF.easeInQuart(t * 2 - d, b + c / 2, c / 2, d)
  },
  /** easeInQuint */
  easeInQuint: function (t: number, b = 0, c = 1, d = 1) {
    return c * (t /= d) * t * t * t * t + b
  },
  /** easeOutQuint */
  easeOutQuint: function (t: number, b = 0, c = 1, d = 1) {
    return c * ((t = t / d - 1) * t * t * t * t + 1) + b
  },
  /** easeInOutQuint */
  easeInOutQuint: function (t: number, b = 0, c = 1, d = 1) {
    if ((t /= d / 2) < 1) return (c / 2) * t * t * t * t * t + b
    return (c / 2) * ((t -= 2) * t * t * t * t + 2) + b
  },
  /** easeOutInQuint */
  easeOutInQuint: function (t: number, b = 0, c = 1, d = 1) {
    if (t < d / 2) return easingF.easeOutQuint(t * 2, b, c / 2, d)
    return easingF.easeInQuint(t * 2 - d, b + c / 2, c / 2, d)
  },
  /** easeInSine */
  easeInSine: function (t: number, b = 0, c = 1, d = 1) {
    return -c * Math.cos((t / d) * (Math.PI / 2)) + c + b
  },
  /** easeOutSine */
  easeOutSine: function (t: number, b = 0, c = 1, d = 1) {
    return c * Math.sin((t / d) * (Math.PI / 2)) + b
  },
  /** easeInOutSine */
  easeInOutSine: function (t: number, b = 0, c = 1, d = 1) {
    return (-c / 2) * (Math.cos((Math.PI * t) / d) - 1) + b
  },
  /** easeOutInSine */
  easeOutInSine: function (t: number, b = 0, c = 1, d = 1) {
    if (t < d / 2) return easingF.easeOutSine(t * 2, b, c / 2, d)
    return easingF.easeInSine(t * 2 - d, b + c / 2, c / 2, d)
  },
  /** easeInExpo */
  easeInExpo: function (t: number, b = 0, c = 1, d = 1) {
    return t == 0 ? b : c * Math.pow(2, 10 * (t / d - 1)) + b - c * 0.001
  },
  /** easeOutExpo */
  easeOutExpo: function (t: number, b = 0, c = 1, d = 1) {
    return t == d ? b + c : c * 1.001 * (-Math.pow(2, (-10 * t) / d) + 1) + b
  },
  /** easeInOutExpo */
  easeInOutExpo: function (t: number, b = 0, c = 1, d = 1) {
    if (t == 0) return b
    if (t == d) return b + c
    if ((t /= d / 2) < 1) return (c / 2) * Math.pow(2, 10 * (t - 1)) + b - c * 0.0005
    return (c / 2) * 1.0005 * (-Math.pow(2, -10 * --t) + 2) + b
  },
  /** easeOutInExpo */
  easeOutInExpo: function (t: number, b = 0, c = 1, d = 1) {
    if (t < d / 2) return easingF.easeOutExpo(t * 2, b, c / 2, d)
    return easingF.easeInExpo(t * 2 - d, b + c / 2, c / 2, d)
  },
  /** easeInCirc */
  easeInCirc: function (t: number, b = 0, c = 1, d = 1) {
    return -c * (Math.sqrt(1 - (t /= d) * t) - 1) + b
  },
  /** easeOutCirc */
  easeOutCirc: function (t: number, b = 0, c = 1, d = 1) {
    return c * Math.sqrt(1 - (t = t / d - 1) * t) + b
  },
  /** easeInOutCirc */
  easeInOutCirc: function (t: number, b = 0, c = 1, d = 1) {
    if ((t /= d / 2) < 1) return (-c / 2) * (Math.sqrt(1 - t * t) - 1) + b
    return (c / 2) * (Math.sqrt(1 - (t -= 2) * t) + 1) + b
  },
  /** easeOutInCirc */
  easeOutInCirc: function (t: number, b = 0, c = 1, d = 1) {
    if (t < d / 2) return easingF.easeOutCirc(t * 2, b, c / 2, d)
    return easingF.easeInCirc(t * 2 - d, b + c / 2, c / 2, d)
  },

  /** easeInBounce */
  easeInBounce: function (t: number, b = 0, c = 1, d = 1) {
    return c - easingF.easeOutBounce(d - t, 0, c, d) + b
  },
  /** easeOutBounce */
  easeOutBounce: function (t: number, b = 0, c = 1, d = 1) {
    if ((t /= d) < 1 / 2.75) {
      return c * (7.5625 * t * t) + b
    } else if (t < 2 / 2.75) {
      return c * (7.5625 * (t -= 1.5 / 2.75) * t + 0.75) + b
    } else if (t < 2.5 / 2.75) {
      return c * (7.5625 * (t -= 2.25 / 2.75) * t + 0.9375) + b
    } else {
      return c * (7.5625 * (t -= 2.625 / 2.75) * t + 0.984375) + b
    }
  },
  /** easeInOutBounce */
  easeInOutBounce: function (t: number, b = 0, c = 1, d = 1) {
    if (t < d / 2) return easingF.easeInBounce(t * 2, 0, c, d) * 0.5 + b
    else return easingF.easeOutBounce(t * 2 - d, 0, c, d) * 0.5 + c * 0.5 + b
  },
  /** easeOutInBounce */
  easeOutInBounce: function (t: number, b = 0, c = 1, d = 1) {
    if (t < d / 2) return easingF.easeOutBounce(t * 2, b, c / 2, d)
    return easingF.easeInBounce(t * 2 - d, b + c / 2, c / 2, d)
  },
}

export const defaultSpringEase = {
  type: 'spring',
  stiffness: 500,
  damping: 25,
  restSpeed: 10,
}
export const defaultSpringEaseScale = {
  type: 'spring',
  stiffness: 400,
  damping: 13,
  //restSpeed: 10,
  restSpeed: 0.3, //scale対応
}
